import { defineStore } from 'pinia'
import { register } from 'register-service-worker'
import { useUserStore } from '@/stores'
import {
  getToken,
  deleteToken,
  isMessagingSupported
} from '@/modules/messaging'
import { updateUser } from '@/modules/database/users'
import { setStorageItem, removeStorageItem } from '@/modules/native'
import { FirebaseError } from '@/modules/firebase'
import router from '@/router'

const mode = import.meta.env.MODE

interface SystemData {
  isDev: boolean
  platform: Platform | null
  isSmallScreen: boolean
  serviceWorkerRegistration: ServiceWorkerRegistration | null
  isUpdateAvailable: boolean
  notifications: {
    isSupported: boolean
    isGranted: boolean
  }
}

export const useSystemStore = defineStore('system', {
  state: (): SystemData => ({
    isDev: mode === 'development',
    platform: null,
    isSmallScreen: true,
    serviceWorkerRegistration: null,
    isUpdateAvailable: false,
    notifications: {
      isSupported: false,
      isGranted: false
    }
  }),
  actions: {
    async initialisePlatform(platform: Platform = 'web') {
      this.platform = platform
      if (!this.isDev && platform === 'web') {
        await this.registerServiceWorker()
      }
    },
    async registerServiceWorker() {
      if (!('serviceWorker' in navigator)) return

      register('/service-worker.js', {
        ready: () => {
          console.log(
            'App is being served from cache by a service worker.\n' +
              'For more details, visit https://goo.gl/AFskqB'
          )
        },
        registered: (registration: ServiceWorkerRegistration) => {
          this.serviceWorkerRegistration = registration

          console.log('Service worker has been registered.')

          setInterval(
            async () => {
              console.log('Registration updating...')
              return registration.update()
            },
            1000 * 60 * 60
          )
        },
        cached: () => {
          console.log('Content has been cached for offline use.')
        },
        updatefound: (registration: ServiceWorkerRegistration) => {
          console.log('New content is downloading.')

          const newServiceWorker = registration.installing

          if (newServiceWorker) {
            newServiceWorker.addEventListener('statechange', () => {
              if (registration.waiting && navigator.serviceWorker.controller) {
                this.isUpdateAvailable = true
              }
            })
          }
        },
        updated: () => {
          console.log('New content is available; please refresh.')
        },
        offline: () => {
          console.log(
            'No internet connection found. App is running in offline mode.'
          )
        },
        error: (error: Error) => {
          console.error('Error during service worker registration:', error)
        }
      })

      navigator.serviceWorker.addEventListener('message', async (payload) => {
        const messagePayload = payload?.data?.firebaseMessaging?.payload
        if (!messagePayload) return
        const path = messagePayload.fcmOptions?.link
        if (path && router.currentRoute.value.path !== path) {
          await router.push({ path })
        }
      })

      let isRefreshing = false

      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!isRefreshing) {
          window.location.reload()
          isRefreshing = true
        }
      })
    },
    async isMessagingSupported() {
      const isSupported =
        !this.isDev &&
        'serviceWorker' in navigator &&
        (await isMessagingSupported())
      this.notifications.isSupported = isSupported
      if (isSupported) {
        this.notifications.isGranted = Notification.permission === 'granted'
      }
      return isSupported
    },
    async subscribeMessaging(userId: string) {
      const userStore = useUserStore()

      if (this.isDev) {
        console.warn('Push notifications are disabled in development mode.')
        return
      }

      let registration = this.serviceWorkerRegistration
      if (!registration && 'serviceWorker' in navigator) {
        registration = await navigator.serviceWorker.ready
        this.serviceWorkerRegistration = registration
      }

      if (!registration) {
        throw new Error('Service worker registration not found')
      }

      let permission = Notification.permission
      if (permission === 'default') {
        permission = await Notification.requestPermission()
      }

      if (permission !== 'granted') {
        throw new FirebaseError(
          'messaging/permission-blocked',
          'Permission was not granted'
        )
      }

      const token = await getToken(registration)

      if (userStore.userData && userStore.userData.messagingToken === token) {
        this.notifications.isGranted = true
        return
      }

      if (userStore.userData) {
        userStore.userData.messagingToken = token
      }
      await updateUser(userId, {
        messagingToken: token,
        'preferences.notifications.enabled': true
      })
      await setStorageItem('deviceToken', token)
      this.notifications.isGranted = true
    },
    async unsubscribeMessaging(userId: string) {
      const userStore = useUserStore()

      if (userStore.userData) {
        userStore.userData.messagingToken = ''
      }
      await updateUser(userId, {
        messagingToken: '',
        'preferences.notifications.enabled': false
      })
      await removeStorageItem('deviceToken')

      try {
        await deleteToken()
      } catch (error) {
        console.error('Error deleting messaging token:', error)
      }

      this.notifications.isGranted = false
    }
  }
})
